import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader } from "../components/Shell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { WipeSectionButton } from "../components/DeleteButton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Warning, ShieldWarning, Trash, Key } from "@phosphor-icons/react";
import PinChangeDialog from "../components/PinChangeDialog";

const SECTIONS = [
  { key: "dancers", label: "les danseuses et toutes leurs activités (bookings, comptes, demandes, règlements)", title: "Danseuses" },
  { key: "bookings", label: "tous les bookings et les comptes journaliers associés", title: "Bookings" },
  { key: "accounts", label: "tous les comptes journaliers", title: "Comptes journaliers" },
  { key: "payment_requests", label: "toutes les demandes de règlement et leurs paiements", title: "Demandes de règlement" },
  { key: "payments", label: "tous les règlements effectués", title: "Règlements" },
  { key: "staff", label: "tout le personnel et ses pointages", title: "Personnel" },
  { key: "timesheets", label: "tous les pointages", title: "Pointages" },
  { key: "daily_cash", label: "toute la caisse journalière", title: "Caisse journalière" },
  { key: "reports", label: "tous les rapports archivés", title: "Rapports" },
];

export default function Settings() {
  const { user } = useAuth();
  const [wipeAllOpen, setWipeAllOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [typedConfirm, setTypedConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);

  if (user?.role !== "admin") {
    return (
      <div>
        <PageHeader testId="settings-header" title="Réglages" subtitle="Réservé au Patron." />
        <div className="surface p-6 text-[#a1a1aa]">Accès réservé au Patron.</div>
      </div>
    );
  }

  const closeWipeAll = () => { setWipeAllOpen(false); setStep(1); setTypedConfirm(""); };

  const doWipeAll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/wipe-all");
      const total = Object.values(r.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Base vidée — ${total} éléments supprimés`);
      closeWipeAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        testId="settings-header"
        title="Réglages"
        subtitle="Gestion des données et sécurité du compte."
      />

      {/* Sécurité */}
      <div className="surface p-6 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Key size={18} color="#fbbf24" weight="fill" />
          <div className="font-display text-lg">Sécurité</div>
        </div>
        <div className="text-sm text-[#a1a1aa] mb-4">
          Changer les codes PIN d'accès back-office. Un code de confirmation est
          envoyé à <b className="text-white">letantra972@gmail.com</b>.
        </div>
        <Button
          onClick={() => setPinOpen(true)}
          className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black"
          data-testid="open-pin-change"
        >
          <Key size={16} className="mr-2" /> Changer un code PIN
        </Button>
      </div>

      {/* Zone danger */}
      <div className="surface p-6 border-[#7f1d1d]/40 border-2 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <ShieldWarning size={20} color="#f87171" weight="fill" />
          <div className="font-display text-lg">Zone danger</div>
        </div>
        <div className="text-sm text-[#a1a1aa] mb-5">
          Suppression par section. Les actions sont <b className="text-[#f87171]">irréversibles</b>.
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SECTIONS.map((s) => (
            <div
              key={s.key}
              className="flex items-center justify-between p-3 rounded-md border border-[#27272a] bg-[#09090b]"
            >
              <div>
                <div className="font-medium">{s.title}</div>
                <div className="text-xs text-[#71717a] mt-1">Supprime {s.label}.</div>
              </div>
              <WipeSectionButton
                section={s.key}
                label={s.label}
                testId={`wipe-${s.key}`}
              />
            </div>
          ))}
        </div>

        {/* Wipe ALL */}
        <div className="mt-6 p-4 rounded-md border-2 border-[#dc2626]/40 bg-[#1a0b0b]">
          <div className="flex items-start gap-3">
            <Warning size={24} color="#dc2626" weight="fill" className="mt-1" />
            <div className="flex-1">
              <div className="font-display text-base text-[#f87171] mb-1">
                Vider toute l'application
              </div>
              <div className="text-xs text-[#a1a1aa] mb-3">
                Supprime <b className="text-white">absolument toutes</b> les données métier (danseuses,
                bookings, comptes, demandes, règlements, personnel, pointages,
                caisse, rapports). Seuls les comptes Patron et Manageuse sont
                préservés. Parfait pour un redémarrage propre.
              </div>
              <Button
                onClick={() => setWipeAllOpen(true)}
                className="bg-[#dc2626] hover:bg-[#991b1b] text-white"
                data-testid="wipe-all-open"
              >
                <Trash size={16} className="mr-2" /> Vider toute l'application
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Wipe-all triple confirmation: warn → confirm → type VIDER */}
      <AlertDialog open={wipeAllOpen} onOpenChange={(v) => { if (!v) closeWipeAll(); }}>
        <AlertDialogContent className="bg-[#121217] border-[#27272a] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl flex items-center gap-2">
              <Warning size={22} color="#f87171" weight="fill" />
              {step === 1 && "Tout vider ?"}
              {step === 2 && "Confirmation finale"}
              {step === 3 && "Tapez VIDER pour confirmer"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#a1a1aa]">
              {step === 1 && (
                <>
                  Cette action va <b className="text-[#f87171]">supprimer toutes les données</b> de
                  l'application. Les comptes Patron et Manageuse seront conservés.
                  <br /><br /> Action <b>irréversible</b>.
                </>
              )}
              {step === 2 && (
                <>Êtes-vous <b className="text-[#f87171]">absolument certain</b> ? C'est votre dernière chance d'annuler.</>
              )}
              {step === 3 && (
                <>
                  Pour éviter toute erreur, veuillez taper le mot <b className="text-[#f87171]">VIDER</b> (en majuscules) ci-dessous.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {step === 3 && (
            <Input
              autoFocus
              value={typedConfirm}
              onChange={(e) => setTypedConfirm(e.target.value)}
              placeholder="Tapez VIDER"
              className="bg-[#09090b] border-[#7f1d1d] text-white placeholder:text-[#71717a] focus-visible:ring-[#dc2626]"
              data-testid="wipe-all-type"
            />
          )}

          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-[#27272a] text-white hover:bg-[#1a1a21]"
              data-testid="wipe-all-cancel"
              onClick={closeWipeAll}
            >
              Annuler
            </AlertDialogCancel>
            {step === 1 && (
              <AlertDialogAction
                className="bg-[#f87171] hover:bg-[#ef4444] text-white"
                data-testid="wipe-all-next"
                onClick={(e) => { e.preventDefault(); setStep(2); }}
              >
                Continuer
              </AlertDialogAction>
            )}
            {step === 2 && (
              <AlertDialogAction
                className="bg-[#dc2626] hover:bg-[#991b1b] text-white"
                data-testid="wipe-all-next-2"
                onClick={(e) => { e.preventDefault(); setStep(3); }}
              >
                J'ai compris, continuer
              </AlertDialogAction>
            )}
            {step === 3 && (
              <AlertDialogAction
                disabled={busy || typedConfirm !== "VIDER"}
                className="bg-[#dc2626] hover:bg-[#991b1b] text-white disabled:opacity-40"
                data-testid="wipe-all-confirm"
                onClick={doWipeAll}
              >
                {busy ? "Suppression..." : "Oui, tout vider"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PinChangeDialog open={pinOpen} onOpenChange={setPinOpen} />
    </div>
  );
}
